package dev.lingvo.site.components.editor

import kotlinx.browser.window

internal fun EditorWorkspaceState.openUtility(
    surface: EditorUtilitySurface,
    invoker: EditorFocusRef,
    motionPolicy: EditorUtilityMotionPolicy? = null,
    actionOrigin: EditorUtilityActionOrigin = EditorUtilityActionOrigin.KEYBOARD,
): EditorTransition {
    require(surface != EditorUtilitySurface.NONE)
    val resolvedMotionPolicy = motionPolicy ?: editorUtilityMotionPolicy(
        actionOrigin = actionOrigin,
        reducedMotion = editorUtilityPrefersReducedMotion(),
    )
    val opening = utility == EditorUtilitySurface.NONE
    val currentPresence = resolvedUtilityPresence()
    val reversing = currentPresence.phase == EditorUtilityPresencePhase.EXITING
    if (!opening && !reversing) {
        return copy(
            utility = surface,
            utilityReturnFocus = invoker,
        ).asTransition()
    }
    val nextPresence = currentPresence.requestOpen(
        policy = resolvedMotionPolicy,
        alreadyMounted = !opening,
    )
    val compactFocus = EditorDeferredFocus(
        EditorFocusRef.ElementId(EDITOR_UTILITY_CLOSE_ID),
        invoker,
    )
    val compactPresentation = renderedLayout() == EditorRenderedLayout.COMPACT
    val waitForCompactEntry = compactPresentation &&
        nextPresence.phase == EditorUtilityPresencePhase.PENDING_ENTER
    val measuringWorkbenchEntry = viewport == EditorViewportClass.WORKBENCH && opening
    val nextState = copy(
        primaryView = EditorPrimaryView.EDITOR,
        queueMode = EditorQueueMode.LIST,
        utility = surface,
        utilityReturnFocus = invoker,
        utilityPresence = nextPresence,
        utilityOpenFocus = compactFocus.takeIf { waitForCompactEntry || measuringWorkbenchEntry },
        utilityCloseFocus = null,
        filtersReturnFocus = null,
        overflowOpen = false,
        overflowReturnFocus = null,
        focusAfterQueueRows = null,
    )
    if (viewport == EditorViewportClass.WORKBENCH && opening) {
        // **On the workbench the utility is a sidecar, not a modal.** It takes a column beside the
        // editor rather than covering it, so it must not take the caret either — and opening it
        // restarts measurement, which remounts the editor pane and drops focus on `<body>` unless
        // focus is carried across explicitly. It was not, so Alt+H did the exact thing its own
        // shortcut definition exists to prevent: "Open history without leaving the field."
        //
        // The invoker is the right destination for both callers. Alt+H captures the caret's real
        // position, so the translator lands back mid-sentence; the toolbar button captures itself,
        // which is what a non-modal popover should do. The close button is only the fallback, for
        // when whatever invoked this is no longer in the document.
        val deferredFocus = if (renderedLayout() == EditorRenderedLayout.COMPACT) {
            compactFocus
        } else {
            EditorDeferredFocus(
                invoker,
                EditorFocusRef.ElementId(EDITOR_UTILITY_CLOSE_ID),
            )
        }
        return nextState.restartMeasurement(deferredFocus).asTransition()
    }
    if (nextState.renderedLayout() == EditorRenderedLayout.COMPACT && !waitForCompactEntry) {
        return nextState.emitOrDeferFocus(compactFocus, restoreQueue = false)
    }
    return nextState.asTransition()
}
internal fun editorUtilityMotionPolicy(
    actionOrigin: EditorUtilityActionOrigin,
    reducedMotion: Boolean,
): EditorUtilityMotionPolicy = when {
    reducedMotion -> EditorUtilityMotionPolicy.INSTANT
    actionOrigin == EditorUtilityActionOrigin.POINTER -> EditorUtilityMotionPolicy.STANDARD
    else -> EditorUtilityMotionPolicy.INSTANT
}

private fun editorUtilityPrefersReducedMotion(): Boolean = runCatching {
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
}.getOrDefault(false)

internal fun EditorWorkspaceState.advanceUtilityPresence(
    requestGeneration: Long,
    settleImmediately: Boolean = false,
): EditorTransition {
    if (utility == EditorUtilitySurface.NONE) return EditorTransition(this)
    val current = resolvedUtilityPresence()
    val advanced = current.advanceEnter(
        requestGeneration = requestGeneration,
        settleImmediately = settleImmediately ||
            current.motionPolicy == EditorUtilityMotionPolicy.INSTANT ||
            editorUtilityPrefersReducedMotion(),
    )
    if (advanced == current) return EditorTransition(this)

    val entryBecameInteractive =
        current.phase == EditorUtilityPresencePhase.PENDING_ENTER &&
            advanced.phase in setOf(
                EditorUtilityPresencePhase.ENTERING,
                EditorUtilityPresencePhase.PRESENT,
            )
    val deferredFocus = utilityOpenFocus.takeIf { entryBecameInteractive }
    return copy(
        utilityPresence = advanced,
        utilityOpenFocus = if (entryBecameInteractive) null else utilityOpenFocus,
    ).emitOrDeferFocus(deferredFocus, restoreQueue = false)
}

internal fun EditorWorkspaceState.switchUtility(
    surface: EditorUtilitySurface,
    actionOrigin: EditorUtilityActionOrigin = EditorUtilityActionOrigin.POINTER,
): EditorTransition {
    require(surface != EditorUtilitySurface.NONE)
    if (utility == EditorUtilitySurface.NONE) return EditorTransition(this)
    val switched = if (utility == surface) this else copy(utility = surface)
    return if (
        actionOrigin == EditorUtilityActionOrigin.KEYBOARD &&
        switched.utilityPresencePhase() in setOf(
            EditorUtilityPresencePhase.PENDING_ENTER,
            EditorUtilityPresencePhase.ENTERING,
        )
    ) {
        switched.advanceUtilityPresence(
            requestGeneration = switched.utilityPresenceGeneration,
            settleImmediately = true,
        )
    } else {
        switched.asTransition()
    }
}

internal fun EditorWorkspaceState.closeUtility(
    activeTarget: EditorFocusRef.Target?,
    actionOrigin: EditorUtilityActionOrigin = EditorUtilityActionOrigin.KEYBOARD,
    settleImmediately: Boolean = false,
): EditorTransition {
    if (utility == EditorUtilitySurface.NONE) return EditorTransition(this)
    val current = resolvedUtilityPresence()
    if (current.phase == EditorUtilityPresencePhase.EXITING) {
        return EditorTransition(this)
    }
    val deferredFocus = utilityReturnFocus?.let { EditorDeferredFocus(it, activeTarget) }
    return requestUtilityClose(
        deferredFocus = deferredFocus,
        settleImmediately = settleImmediately ||
            actionOrigin == EditorUtilityActionOrigin.KEYBOARD ||
            editorUtilityPrefersReducedMotion(),
    )
}

private fun EditorWorkspaceState.requestUtilityClose(
    deferredFocus: EditorDeferredFocus?,
    settleImmediately: Boolean,
): EditorTransition {
    val requested = resolvedUtilityPresence().requestExit(settleImmediately)
    if (!settleImmediately) {
        return copy(
            utilityPresence = requested,
            utilityOpenFocus = null,
            utilityCloseFocus = deferredFocus,
            focusAfterQueueRows = null,
            focusAfterMeasurement = null,
        ).asTransition()
    }
    return commitUtilityClose(requested, deferredFocus)
}

internal fun EditorWorkspaceState.completeUtilityExit(
    requestGeneration: Long,
): EditorTransition {
    if (utility == EditorUtilitySurface.NONE) return EditorTransition(this)
    val current = resolvedUtilityPresence()
    val completed = current.finishExit(requestGeneration)
    if (completed == current) return EditorTransition(this)
    val deferredFocus = utilityCloseFocus ?: utilityReturnFocus?.let {
        EditorDeferredFocus(it, null)
    }
    return commitUtilityClose(completed, deferredFocus)
}

private fun EditorWorkspaceState.commitUtilityClose(
    completedPresence: EditorUtilityPresenceState,
    deferredFocus: EditorDeferredFocus?,
): EditorTransition {
    val nextState = copy(
        primaryView = EditorPrimaryView.EDITOR,
        queueMode = EditorQueueMode.LIST,
        utility = EditorUtilitySurface.NONE,
        utilityReturnFocus = null,
        utilityPresence = completedPresence,
        utilityOpenFocus = null,
        utilityCloseFocus = null,
        focusAfterQueueRows = null,
    )
    if (viewport == EditorViewportClass.WORKBENCH) {
        return nextState.restartMeasurement(deferredFocus).asTransition()
    }
    return nextState.emitOrDeferFocus(
        deferredFocus,
        restoreQueue = nextState.mountsQueueList() && !mountsQueueList(),
    )
}

internal fun EditorWorkspaceState.closeUtilityForInsertion(
    target: EditorFocusRef.Target,
    actionOrigin: EditorUtilityActionOrigin = EditorUtilityActionOrigin.KEYBOARD,
): EditorTransition {
    if (utility == EditorUtilitySurface.NONE) return EditorTransition(this)
    val current = resolvedUtilityPresence()
    if (current.phase == EditorUtilityPresencePhase.EXITING) {
        return EditorTransition(this)
    }
    val deferredFocus = utilityReturnFocus?.let { EditorDeferredFocus(target, it) }
        ?: EditorDeferredFocus(target, null)
    return requestUtilityClose(
        deferredFocus = deferredFocus,
        settleImmediately = actionOrigin == EditorUtilityActionOrigin.KEYBOARD ||
            editorUtilityPrefersReducedMotion(),
    )
}
